import { LanguageTag } from '@flipster/i18n/types';
import { V2OrderSideType } from '@flipster/utils/types/base';
import { valueof } from '@flipster/utils/typescript';

import { ResPagination, Timestamp } from '../../types';
import { CommunityFeedItemWithSymbol } from '../community/types';
import { CopyTradePortfolioAvailabilityStatus, CopyTradePortfolioPnlHistory } from '../copy-trade/types';
import { KycLevelType } from '../kyc/types';
import { MfaScope } from '../mfa/types';

export interface User {
  uid: string;
  accountId?: number;
  createdAt: string;
  email: string;
  emailVerified: boolean;
  firstDepositAt?: string;
  kycLevel?: Exclude<KycLevelType, 'LEVEL_0'>;
  kycActivatedAt?: string;
  lang: LanguageTag;
  redirectLocale: LanguageTag;
  lastLoginAt?: string;
  lastLoginDevice?: Device;
  name?: string;
  nickname?: string;
  participatedCompetitionUrl?: string;
  phoneNumber?: string;
  phoneCountryCode?: string;
  tfaDisabledByAdminAt?: string;
  tfaEnabled: boolean;
  tfaEnabledAt?: string;
  tfaRegisterCount: number;
  userId: number;
  profileId: string;
  tier: number;
  hasPassword: boolean;
  isAffiliate?: boolean;
  isCopyTradeMaster?: boolean;
  passwordChangeRequiredAt?: string;
  isInternal?: boolean;
  showLossProtection?: boolean; // KycLevel2Simple 인 경우 loss protection 대상자가 아님
  isPolandOffboard?: boolean;
  country?: string;
  /** User-level global default for margin type. `null` = not set; client falls back to cold-start default (Isolated). */
  defaultMarginType: DefaultMarginType | null;
  /** User-level global default for initial leverage (1..50). `null` = not set; client falls back to cold-start default (1×). */
  defaultLeverage: number | null;
}

export interface GeetestCaptchaResponse {
  lotNumber?: string;
  passToken?: string;
  genTime?: string;
  captchaOutput?: string;
  deviceType?: 'WEBSITE';
}

export interface Auth {
  user: User;
  session: {
    expiresAt: string;
  };
}

export interface RestrictedUserRegion {
  isRestrictedRegion: boolean;
  restrictedRegion: string | null;
}

export interface UserReferralInfo {
  code: string;
  signUpCode?: string | null;
  pendingReferees: number;
  totalReward: string;
}

export const UserBlockServiceEnum = {
  Futures: 'USER_BLOCK_SERVICE_TYPE_PERPETUAL_SWAP_TRADE', // NOTE: 차후에 Pswap으로 바꿔야함
  // Spot: 'USER_BLOCK_SERVICE_TYPE_SPOT_TRADE', // 이제 spot 없음
  Withdrawal: 'USER_BLOCK_SERVICE_TYPE_WITHDRAWAL',
  Deposit: 'USER_BLOCK_SERVICE_TYPE_DEPOSIT',
  Conversion: 'USER_BLOCK_SERVICE_TYPE_CURRENCY_CONVERSION',
  ApiTrade: 'USER_BLOCK_SERVICE_TYPE_API_TRADE',
} as const;

export type UserBlockServiceType = valueof<typeof UserBlockServiceEnum>;

export interface GetUserBlockParams {
  serviceType: UserBlockServiceType[];
}

export const User24HourWithdrawalBlockReasonEnum = {
  PasswordReset: 'PASSWORD_RESET',
  PasswordChange: 'PASSWORD_CHANGE',
  PasskeyUnavailable: 'PASSKEY_UNAVAILABLE',
  WithdrawalLimitChange: 'WITHDRAWAL_LIMIT_CHANGE',
  TotpChange: 'TOTP_CHANGE',
  PhoneChange: 'PHONE_CHANGE',
  FirstDeposit: 'FIRST_DEPOSIT',
  Register2FA: 'REREGISTER_2FA',
} as const;

export const UserBlockReasonEnum = {
  InternalError: 'INTERNAL_ERROR',
  Reregister2FA: 'REREGISTER_2FA',
  NotEnoughKycLevel: 'NOT_ENOUGH_KYC_LEVEL',
  TfaNotEnabled: 'TFA_NOT_ENABLED',
  ...User24HourWithdrawalBlockReasonEnum,
} as const;

export type UserBlockReasonType = valueof<typeof UserBlockReasonEnum>;

export interface UserBlockInfo {
  serviceType: UserBlockServiceType;
  reason: UserBlockReasonType;
  startedAt: string;
  expiredAt: string;
}

export interface GetUserBlockResponse {
  userBlockInfos: UserBlockInfo[];
}

export interface PutUserNicknameParams {
  nickname?: string;
}

export interface PostUserLoginParams {
  email: string;
  password: string;
  captchaResponseToken?: string;
  captchaPlatformType?: 'WEBSITE' | 'ANDROID';
  authMethod?: 'COOKIE' | 'SESSION_KEY';
  geetestCaptchaResponse?: GeetestCaptchaResponse;
  lang: LanguageTag;
}

export interface PostUserLoginResponse {
  requireMfa: boolean;
  sessionKey?: {
    sessionKey: string;
    sessionKeyExpires: string;
  };
  // This only retuerns when requireMfa is true
  authToken?: string;
  // For sms verify guide text
  phoneNumber?: string;
}

export interface PostUserSignupParams {
  email: string;
  password: string;
  passwordConfirmation: string;
  geetestCaptchaResponse?: GeetestCaptchaResponse;
  referralCode?: string;
  lang: LanguageTag;
  country?: string;
  restricted?: boolean;
  marketingConsent?: boolean;
}

export interface PostUserSignupResponse {
  userId: number;
}

export interface PostUserResetPasswordParams {
  email: string;
  newPassword: string;
  newPasswordConfirmation: string;
  resetToken: string;
  mfaSessionToken?: string;
}

export interface PutUserChangePasswordParams {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
  mfaSessionToken?: string;
}

export interface PostUserChangePasswordParams {
  changePasswordLater?: boolean;
}

export interface Device {
  deviceFamily: string;
  osFamily: string;
  uaFamily: string;
}

export interface GetUnmaskedInfoParams {
  name?: boolean;
  phoneNumber?: boolean;
  email?: boolean;
}

export interface UnmaskedInfo {
  userId: number;
  name?: string;
  phoneNumber?: string;
  email?: string;
}

export interface PostUserTotpRequestResponse {
  secret: string;
  provisioningUri: string;
}

export interface PostUserTotpConfirmParams {
  token: string;
  mfaSessionToken: string;
}

export interface DeleteUserTotpDeleteParams {
  mfaSessionToken: string;
}

export interface PostUserSmsRequestResponse {
  sessionToken: string;
  expiresAt: string;
  tokenLength: number;
}

export interface PostUserPhoneChangeParams {
  smsSessionToken: string;
  mfaSessionToken?: string;
}

/**
 * TIER_STATUS_BASIC : 일반 유저
 * TIER_STATUS_VIP : VIP 유저
 * TIER_STATUS_VIP_GRACE : 이전에 VIP 였다가 지금은 조건을 만족하지 못했지만 이전 7일~지금 까지 중에 만족했던 적이 있는 유저(VIP 유저로 간주, 혜택 유지)
 * TIER_STATUS_VIP_TRIAL : VIP 트라이얼 유저
 */
export type UserTierStatus =
  | 'TIER_STATUS_BASIC'
  | 'TIER_STATUS_VIP'
  | 'TIER_STATUS_VIP_GRACE'
  | 'TIER_STATUS_VIP_TRIAL';

export type VipTrialType = 'DEFAULT' | 'LIMITED';

export interface GetUserTierResponse {
  lastVolume: string;
  thresholdVolume: string;
  volumeDays: number;
  status: UserTierStatus;
  lastChangedAt?: string;
  lastVolumeChangedAt?: string;
  /**
   * @deprecated Use lastUsdtBalance24h instead.
   * todo (hoyong): Remove this field when removed from api as well.
   */
  lastUsdtBalance?: string;
  thresholdUsdtBalance?: string;
  lastUsdtBalanceChangedAt?: string;
  validUntilTime?: string; // For now, only for VIP grace status. Show when will be current status is expired(demoted)
  canConvertBonusToUsdt: boolean;
  updateInProgress: boolean; // Display whether tier calculation is in progress
  vipTrialType?: VipTrialType;
  lastUsdtBalance1h?: string;
  lastUsdtBalance24h?: string;
  vipTier: number; // basic user, tier will be 0
  maxVipTier: number;
  boostValidUntil?: string;
  boostLandingUrl?: string;
}

// passkey
export interface PostUserPasskeyAuthenticationOptionsResponse {
  challenge: string;
  timeout: number;
  rpId: string;
  allowCredentials: { type: 'public-key'; id: string; transports: AuthenticatorTransport[] }[];
  userVerification: UserVerificationRequirement;
}

export interface PostUserPasskeyAuthenticationParams {
  body: {
    id: string;
    rawId: string;
    type: string;
    authenticatorAttachment: string;
    response: {
      clientDataJSON: string;
      authenticatorData: string;
      signature: string;
    };
  };
  authMethod: string;
  decodeChallenge: boolean;
}

export interface PostUserPasskeyAuthenticationResponse {
  sessionKey: {
    sessionKey: string;
    sessionKeyExpires: string;
  };
}

export interface PasskeyCredential {
  credentialId: string;
  device: string;
  os: string;
  createdAt: string;
  lastLoggedInAt: string;
  expiredAt: string;
}

export interface DeleteUserPasskeyCredentialInterimResponse {
  credentials: PasskeyCredential[];
}

export interface DeleteUserPasskeyCredentialParams {
  mfaSessionToken: string;
}

export interface DeleteUserPasskeyCredentialResponse {
  credentials: PasskeyCredential[];
}

export interface GetUserPasskeyCredentialsResponse {
  credentials: PasskeyCredential[];
}

export interface PostUserPasskeyRegistrationOptionsParams {
  device: string;
  os: string;
  adid: string;
}

export interface PostUserPasskeyRegistrationOptionsResponse {
  rp: { name: string; icon?: string; id: string };
  user: { name: string; icon?: string; id: string; displayName: string };
  challenge: string;
  pubKeyCredParams: { type: 'public-key'; alg: number }[];
  timeout: number;
  excludeCredentials: [{ type: 'public-key'; id: string; transports: AuthenticatorTransport[] }];
  authenticatorSelection: {
    authenticatorAttachment?: AuthenticatorAttachment;
    requireResidentKey: boolean;
    userVerification?: UserVerificationRequirement;
    residentKey?: ResidentKeyRequirement;
  };
  attestation: AttestationConveyancePreference;
}

export interface PostUserPasskeyRegistrationParams {
  id: string;
  rawId: string;
  type: string;
  authenticatorAttachment?: string | null;
  response: {
    clientDataJSON: string;
    attestationObject: string;
  };
  decodeChallenge: boolean;
  mfaSessionToken: string;
}

/**
 * @see https://github.prex.sh/Prex/prex-core/blob/176f80787c08eb0f8aecd053227e113e0d958875/openapi/api-gateway.yaml#L1986-L2030
 * - path: `/v1/user/point`
 */
export interface GetUserPointResponse {
  points: string;
  updatedAt: string;
  multiplierRules: {
    multiplier: number;
    balanceThreshold: number;
  }[];
  percentilePoints: {
    percentile: number;
    pointAmount: string;
  }[];
}

/**
 * @see https://github.prex.sh/Prex/prex-core/blob/176f80787c08eb0f8aecd053227e113e0d958875/openapi/api-gateway.yaml#L2031-L2103
 * - path: `/v1/user/point/history`
 */
export interface GetUserPointHistoryResponse {
  hasPrev: boolean;
  hasNext: boolean;
  pointHistories: {
    id: number;
    earnedPoints: string;
    updatedAt: string;
  }[];
}

export interface GetUserLossProtectionResponse {
  lossProtection: {
    protectedUsdt: number;
    isEligible: boolean;
  };
}

// profile
export type SocialType = 'X' | 'TELEGRAM' | 'YOUTUBE';
export interface SocialProfile {
  socialType: SocialType;
  iconUrl: string;
  profileUrl: string;
  numOfFollowers?: number;
}
export interface SocialConnection {
  socialName: 'X';
  username: string;
}
export type PerformanceOverviewPeriod = '3d' | '7d' | '30d';
export type MasterPerformanceOverviewPeriod = '7d' | '30d' | '90d';
export interface PerformancePosition {
  symbol: string;
  // context: https://prextech.slack.com/archives/C087CLN6AKD/p1739753532662939
  side: Uppercase<V2OrderSideType>;
  amount: string;
  pnl: string;
  pnlPct: string;
}
export interface PerformanceByPeriod {
  period: PerformanceOverviewPeriod;
  positionSize: string;
  pnl: string;
  pnlPct: string;
  topPnlPositions: PerformancePosition[];
}
export interface PerformanceOverview {
  updatedAt: string;
  bestPerformancePeriod: PerformanceOverviewPeriod;
  performanceByPeriod: PerformanceByPeriod[];
}
export interface MasterPerformanceOverview {
  updatedAt: string;
  performanceByPeriod: MasterPortfolioPerformanceByPeriod[];
}
export interface MasterPortfolioPerformanceByPeriod {
  period: MasterPerformanceOverviewPeriod;
  totalPnlInUsdt: string;
  totalRoiPct: string;
  totalCopiersPnlInUsdt?: string;
  sharpeRatio: string;
  maxDrawdownPct: string;
  winRatePct: string;
  winCount: number;
  loseCount: number;
  totalVolumeInUsdt: string;
  turnoverRate: string;
  topPnlPositions: TopPnlPosition[];
  dailyPnls: CopyTradePortfolioPnlHistory[];
  assetPreferences: AssetPreference[];
}
export interface TopPnlPosition {
  symbol: string;
  side: V2OrderSideType;
  amount: string;
  pnl: string;
  pnlPct: string;
}
export interface AssetPreference {
  asset: string;
  volumePct: string;
}

export type ProfileSettingVisibility = 'PUBLIC' | 'PRIVATE';
export interface ProfileSettings {
  profileVisibility: ProfileSettingVisibility;
  performanceVisibility: ProfileSettingVisibility;
  tradeHistoryVisibility: ProfileSettingVisibility;
}
export type UserProfileNotificationType = 'TRADING' | 'POST';
export interface UserProfileNotificationItem {
  notificationType: UserProfileNotificationType;
  enabled: boolean;
}
export interface UserProfile {
  nickname: string;
  tier?: UserTierStatus; // TODO [gaeun]: will be removed with vipStatus flag
  vipTier?: number;
  weeklyLeaderboardRank?: number;
  tradedInLast7Days?: boolean;
  weeklyTop10Count: number;
  isInfluencer: boolean;
  introduction?: string;
  socialProfiles: SocialProfile[];
  performanceOverview: PerformanceOverview;
  mySettings: ProfileSettings;
  settings: ProfileSettings;
  isMyProfile: boolean;
  profileId: string;
  notifications: UserProfileNotificationItem[];
  isFollowing: boolean;
  followersCount: number;
  followingCount: number;
  // copy trading: master trader
  copyTradeMasterProfile?: CopyTraderMasterProfile;
  copyTradeMasterPerformance?: MasterPerformanceOverview;
  // for my profile only
  socialConnections?: SocialConnection[];
  // for BOMBIE promotion only
  bombiePromotionCode?: string;
}
export interface CopyTraderMasterProfile {
  portfolioId: string;
  profileIconUrl: string;
  currentCopierCount: number;
  maxCopierCount: number;
  availabilityStatus: CopyTradePortfolioAvailabilityStatus;
  profitSharingPct: string;
  currentAumInUsdt: string;
  interviewHeadline?: string;
  interviewPostUrl?: string;
  aiInsightsText?: string;
  hasAccess?: boolean;
}
export type GetUserProfileResponse = UserProfile & { isMyProfile: true };
export type GetUserProfileByProfileIdResponse = UserProfile;

export interface PutUserProfileParams {
  nickname?: string;
  introduction?: string;
  uploadedProfileImageKey?: string | null;
  socialProfiles?: {
    socialType: SocialType;
    profileUrl: string | null;
    numOfFollowers?: number;
  }[];
  settings?: Partial<ProfileSettings>;
}

export interface PostUserProfileImageParams {
  contentType: string;
  fileExtension: string;
}

export interface PostUserProfileImageResponse {
  uploadUrl: string;
  fields: Record<string, string>;
}
export interface PutUserProfileNotificationParams {
  notifications: UserProfileNotificationItem[];
}

export type TradingActivity = OpenedTradingActivity | ClosedTradingActivity;
export type TradingActivityPositionStatus = 'OPEN' | 'CLOSED';
interface BaseTradingActivity {
  positionId: string;
  transactTime: Timestamp;
  symbol: string;
  initLeverage: number;
  orderSide: Uppercase<V2OrderSideType>;
  orderQty: string;
  /**
   * Whether the trade is about (partial) closing of an existing position.
   * true - closePrice, realizedPnl and realizedPnlPct are not null. openPrice maybe either null or not null.
   * false - openPrice is not null. closePrice is null.
   */
  closeTrade: boolean;
  positionStatus: TradingActivityPositionStatus;
}
export interface OpenedTradingActivity extends BaseTradingActivity {
  closeTrade: false;
  closePrice: undefined;
  realizedPnl: undefined;
  realizedPnlPct: undefined;
  openPrice: string;
}
export interface ClosedTradingActivity extends BaseTradingActivity {
  closeTrade: true;
  closePrice: string;
  realizedPnl: string;
  realizedPnlPct: string;
  // openPrice can be null under one-way mode
  // context: https://prextech.slack.com/archives/C087CLN6AKD/p1740034475765109
  openPrice: string | undefined;
}
export interface GetTradingActivityResponse extends ResPagination {
  tradingActivities: TradingActivity[];
}

export interface GetUserFeedsResponse extends ResPagination {
  feedItems: CommunityFeedItemWithSymbol[];
}

export interface GetTranslatedProfileResponse {
  introduction: string;
  copyTradeMasterProfile: {
    interviewHeadline: string;
    aiInsightsText: string;
  };
}

// profile - oauth
export interface PostUserProfileSocialConnectXParams {
  code: string;
  codeVerifier: string;
  redirectUri: string;
}

export interface PostUserProfileSocialConnectXResponse {
  xUsername: string;
}

// follow
export interface UserProfileFollowItem {
  userProfileId: string;
  nickname: string;
  isFollowing: boolean;
  isVip: boolean;
  weeklyLeaderboardRank?: number;
  followedAt: string;
  vipTier?: number;
}
export interface GetUserFollowersResponse extends ResPagination {
  users: UserProfileFollowItem[];
}
export interface GetUserFollowingResponse extends ResPagination {
  users: UserProfileFollowItem[];
}

export interface GetUserSecurityAuthModeResponse {
  mfaEnabled: boolean;
}

export interface PutUserSecurityAuthModeParams {
  enableMfa: boolean;
  mfaSessionToken?: string;
}

export interface PostUserAuthMfaConfirmParams {
  authToken: string;
  mfaSessionToken: string;
  lang?: LanguageTag;
}

export interface PostUserAuthMfaConfirmResponse {
  sessionKey: {
    sessionKey: string;
    sessionKeyExpires: string;
  };
}

// ab-test
export interface GetAbTestResponse {
  customSl: 'a' | 'b';
  marketTabTotalBalance: 'a' | 'b';
  topTraderOpenPositionLanding: 'orderForm' | 'tradingActivity';
}

// user devices
export interface UserDevice {
  deviceId: string;
  deviceName: string;
  deviceType: 'WEB_PC' | 'WEB_MOBILE' | 'APP_IOS' | 'APP_ANDROID' | 'UNKNOWN';
  hasActiveSession: boolean;
  currentDevice: boolean;
  lastIpAddress: string;
  lastCountry: string;
  lastCity: string;
  lastLoginAt: Timestamp;
  lastActiveAt: Timestamp;
}

export interface GetUserDevicesResponse extends ResPagination {
  devices: UserDevice[];
}

// marketing
export type PromotionSubscribeChannel = 'EMAIL' | 'PUSH';
export interface GetPromoSubsResponse {
  promotionalSubscriptions: { subscribed: boolean; channel: PromotionSubscribeChannel }[];
}

export interface PostPromoSubsParams {
  promotionalSubscriptions: { subscribe: boolean; channel: PromotionSubscribeChannel }[];
}

export interface Notification {
  id: string;
  createdAt: string;
  page: string;
  pageV2: string;
  path: string;
  title: string;
  body: string;
  isRead: boolean;
  groupColor: string;
  groupName: string;
}

export interface NotificationsResponse {
  nextPageCursor: string | null;
  objects: Notification[];
  newFeatureCount?: number;
}

export interface NotificationGroup {
  code: string;
  name: string;
  description: string;
  isSuppressed: boolean;
}

export interface UpdateNotificationGroupReq {
  isSuppressed: boolean;
}

// usd1
export interface GetUsd1PointResponse {
  totalPoints: number;
  pointBreakdown: Usd1PointBreakdown[];
}

export interface Usd1PointBreakdown {
  points: number;
  pointsType: 'TRADING' | 'HOLDING';
}

export interface GetUsd1PointHistoryParams {
  startTime?: Timestamp;
  endTime?: Timestamp;
  cursor?: string;
  count?: number;
}

export interface GetUsd1PointHistoryResponse {
  hasNext: boolean;
  pointHistories: Usd1PointHistory[];
}

export interface Usd1PointHistory {
  id: number;
  pointsType: 'TRADING' | 'HOLDING';
  totalPoints: number;
  originalPoints: number;
  createdAt: Timestamp;
}

// feature updates
export interface FeatureUpdate {
  title: string;
  description: string;
  imageUrl: string;
  pageLink: string;
}

export interface GetFeatureUpdatesResponse {
  features: FeatureUpdate[];
}

export interface FeatureHighlight {
  title: string;
  description: string;
  buttonText: string;
  imageUrl: string;
  pageLink: string;
}

export interface GetFeatureHighlightsResponse {
  highlight?: FeatureHighlight;
  version?: number;
  hasMoreFeatures?: boolean;
}

export interface GetUserVerificationResponse {
  authMethod: 'SESSION' | 'MFA';
  note?: string;
  mfaScope?: MfaScope;
}

export interface PostUserVerificationParams {
  requestId: string;
  authenticate: boolean; // true to approve, false to reject
  mfaSessionToken?: string;
}

// trade defaults
export type DefaultMarginType = 'Isolated' | 'Cross';

export interface PutUserTradeDefaultsParams {
  defaultLeverage: number;
  defaultMarginType: DefaultMarginType;
}

// At least one flag must be true
export interface DeleteUserTradeDefaultsParams {
  resetMarginType?: boolean;
  resetLeverage?: boolean;
}
